import { useCallback, useEffect, useRef, useState } from "react";
import { useWaitForTransactionReceipt, useWriteContract } from "wagmi";
import type { Hash } from "viem";

export type TxPhase = "idle" | "wallet" | "pending" | "success" | "rejected" | "error" | "reverted";

export function useTxFlow(onMined?: () => unknown | Promise<unknown>) {
  const { writeContractAsync } = useWriteContract();
  const [phase, setPhase] = useState<TxPhase>("idle");
  const [hash, setHash] = useState<Hash | undefined>();
  const [label, setLabel] = useState("");
  const [error, setError] = useState("");
  const minedCallbackHash = useRef<Hash | undefined>();

  const receipt = useWaitForTransactionReceipt({ hash, query: { enabled: !!hash } });

  useEffect(() => {
    if (!hash || phase !== "pending") return;

    if (receipt.isSuccess && receipt.data?.status === "success") {
      setPhase("success");
      if (minedCallbackHash.current !== hash) {
        minedCallbackHash.current = hash;
        Promise.resolve(onMined?.()).catch((err) => {
          console.error("[tx-flow] refetch after mined tx failed", err);
        });
      }
    } else if (receipt.isSuccess && receipt.data?.status === "reverted") {
      setPhase("reverted");
      setError("Transaction reverted on-chain");
    } else if (receipt.isError) {
      setPhase("error");
      setError(receipt.error?.message ?? "Receipt error");
    }
  }, [hash, onMined, phase, receipt.data?.status, receipt.error, receipt.isError, receipt.isSuccess]);

  const run = useCallback(
    async (l: string, send: () => Promise<Hash>) => {
      setLabel(l);
      setError("");
      setHash(undefined);
      minedCallbackHash.current = undefined;
      setPhase("wallet");
      try {
        const h = await send();
        setHash(h);
        setPhase("pending");
      } catch (err) {
        const msg = err instanceof Error ? err.message : String(err);
        setPhase(msg.toLowerCase().includes("user rejected") ? "rejected" : "error");
        setError(msg);
      }
    },
    [],
  );

  const reset = useCallback(() => {
    setPhase("idle");
    setHash(undefined);
    minedCallbackHash.current = undefined;
    setLabel("");
    setError("");
  }, []);

  return { phase, hash, label, error, run, reset, writeContractAsync };
}
